import React, { ReactElement, ReactNode, useCallback } from 'react';
import cx from 'classnames';
import type {
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  GroupProps,
  LoadingIndicatorProps,
  MenuListProps,
  MultiValueProps,
  MultiValueRemoveProps,
  OptionProps,
  PropsValue,
  SelectInstance,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';
import { components } from 'react-select';
import type { AsyncCreatableProps } from 'react-select/async-creatable';
import CreatableSelect from 'react-select/async-creatable';

export * from 'react-select';

import { Check, Close, ArrowDown } from '../../Icons';

import FormControl from '../FormControl';

export type SelectOptionT = {
  readonly label?: any;
  readonly value?: string | number;
  readonly options?: readonly SelectOptionT[];
  readonly __isNew__?: boolean;
};

export interface FormSelectProps
  extends Omit<
    AsyncCreatableProps<SelectOptionT, true, GroupBase<SelectOptionT>>,
    'noOptionsMessage'
  > {
  label?: string;
  options?: SelectOptionT[];
  defaultValue?: PropsValue<SelectOptionT>;
  className?: string;
  inputClassName?: string;
  menuClassName?: string;
  size?: 'sm' | 'md';
  isError?: boolean;
  helperText?: ReactNode | string;
  disabled?: boolean;
  readOnly?: boolean;
  isCreatable?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  showSelectedIcon?: boolean;
  noOptionsMessage?: string;
  dropdownIndicator?: ReactElement;
  renderValue?: (data: SelectOptionT) => ReactNode;
  renderOption?: (data: SelectOptionT) => ReactElement;
  renderGroup?: (props: GroupProps) => ReactElement;
  loadOptions?: (inputValue: string) => Promise<SelectOptionT[]> | void;
  name?: string;
}

const FormSelect = React.forwardRef<
  SelectInstance<SelectOptionT, true, GroupBase<SelectOptionT>>,
  FormSelectProps
>(
  (
    {
      options,
      className,
      inputClassName,
      menuClassName,
      menuPortalTarget,
      label,
      size,
      helperText,
      isError,
      disabled,
      readOnly,
      isClearable,
      defaultValue,
      isCreatable,
      isSearchable,
      isLoading,
      noOptionsMessage = 'No Options',
      dropdownIndicator,
      showSelectedIcon,
      required,
      cacheOptions = true,
      renderValue,
      renderOption,
      renderGroup,
      loadOptions,
      name,
      ...props
    },
    ref
  ) => {
    const portal =
      typeof window !== 'undefined' &&
      (document.getElementById('onyx-portal-root') as HTMLElement);

    const defaultLoadOptions = async (inputValue: string) =>
      options.filter(
        (i) =>
          i.value
            .toString()
            .toLocaleLowerCase()
            .includes(inputValue.toLowerCase()) ||
          (typeof i.label === 'string' &&
            i.label
              .toString()
              .toLocaleLowerCase()
              .includes(inputValue.toLowerCase()))
      );

    const Control = ({ children, ...props }: ControlProps) => (
      <FormControl
        required={required}
        disabled={disabled}
        error={isError}
        size={size}
        className="w-auto"
      >
        <components.Control {...props}>{children}</components.Control>
        {helperText && (
          <FormControl.HelperText>{helperText}</FormControl.HelperText>
        )}
      </FormControl>
    );

    const ValueContainer = ({ children, ...props }: ValueContainerProps) => {
      return (
        <div className="relative ml-2 flex flex-col justify-center min-h-[inherit]">
          {label && <FormControl.Label>{label}</FormControl.Label>}
          <components.ValueContainer {...props}>
            {children}
          </components.ValueContainer>
        </div>
      );
    };

    const ClearIndicator = ({
      innerProps: { ref, ...restInnerProps },
      clearValue,
    }: ClearIndicatorProps<SelectOptionT, true>) => (
      <div {...restInnerProps} ref={ref}>
        <Close
          className="cursor-pointer"
          width={16}
          height={16}
          onClick={(e) => {
            clearValue();
            e.preventDefault();
          }}
        />
      </div>
    );

    const DropdownIndicator = (
      props: DropdownIndicatorProps<SelectOptionT, false>
    ) => (
      <components.DropdownIndicator {...props}>
        {dropdownIndicator ? (
          dropdownIndicator
        ) : (
          <ArrowDown
            className={cx('cursor-pointer transition-transform', {
              'rotate-180': props.selectProps.menuIsOpen,
              'fill-black opacity-50': props.isDisabled,
            })}
            width={size === 'md' ? 24 : 18}
            height={size === 'md' ? 24 : 18}
          />
        )}
      </components.DropdownIndicator>
    );

    const Group = (props: GroupProps) =>
      renderGroup ? renderGroup(props) : <components.Group {...props} />;

    const SingleValue = ({
      children,
      ...props
    }: SingleValueProps<SelectOptionT, false>) => (
      <components.SingleValue {...props}>
        {renderValue ? <>{renderValue(props.data)}</> : children}
      </components.SingleValue>
    );

    const MultiValue = ({
      children,
      ...props
    }: MultiValueProps<SelectOptionT, true>) => (
      <components.MultiValue {...props}>
        {renderValue ? <>{renderValue(props.data)}</> : children}
      </components.MultiValue>
    );

    const MultiValueRemove = (props: MultiValueRemoveProps) => (
      <components.MultiValueRemove {...props}>
        <Close className="cursor-pointer" width={16} height={16} />
      </components.MultiValueRemove>
    );

    const Option = ({ children, ...props }: OptionProps) =>
      renderOption ? (
        <div
          ref={props.innerRef}
          {...props.innerProps}
          className={cx(
            'flex items-center px-3 justify-between cursor-pointer',
            {
              'bg-pearl-grey': props.isFocused || props.isSelected,
            }
          )}
        >
          <div className="truncate flex-1">
            {props.data['__isNew__'] === true
              ? children
              : renderOption(props.data)}
          </div>
          {showSelectedIcon && props.isSelected && (
            <Check width={16} height={16} className="w-4" />
          )}
        </div>
      ) : (
        <components.Option
          {...props}
          className={cx({
            'bg-pearl-grey': props.isFocused || props.isSelected,
          })}
        >
          <div className="truncate flex-1">{children}</div>
          {showSelectedIcon && props.isSelected && (
            <Check width={16} height={16} className="w-4" />
          )}
        </components.Option>
      );

    const LoadingIndicator = (props: LoadingIndicatorProps<SelectOptionT>) => (
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className={cx('mr-1', { 'animate-spin': props.selectProps.isLoading })}
      >
        <path
          d="M14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0C10.866 0 14 3.13401 14 7ZM1.39667 7C1.39667 10.0946 3.90537 12.6033 7 12.6033C10.0946 12.6033 12.6033 10.0946 12.6033 7C12.6033 3.90537 10.0946 1.39667 7 1.39667C3.90537 1.39667 1.39667 3.90537 1.39667 7Z"
          fill="#CFCFCF"
        />
        <path
          d="M7 0C7.9404 1.12142e-08 8.87116 0.189485 9.73671 0.557142C10.6023 0.9248 11.3849 1.46309 12.0378 2.13988C12.6907 2.81667 13.2006 3.61808 13.5369 4.49627C13.8733 5.37446 14.0292 6.31142 13.9955 7.25121L12.5997 7.20109C12.6267 6.44881 12.5019 5.69879 12.2326 4.99583C11.9634 4.29286 11.5553 3.65135 11.0326 3.10959C10.51 2.56784 9.88352 2.13695 9.19067 1.84265C8.49782 1.54835 7.75277 1.39667 7 1.39667V0Z"
          fill="#313131"
        />
      </svg>
    );

    const MenuList = ({ children, ...props }: MenuListProps<SelectOptionT>) => (
      <components.MenuList {...props}>{children}</components.MenuList>
    );

    return (
      <CreatableSelect
        {...props}
        className={cx('input-select', size, className && className)}
        classNames={{
          input: () => inputClassName,
          menu: () => menuClassName,
        }}
        cacheOptions={cacheOptions}
        defaultOptions={options ?? true}
        defaultValue={defaultValue}
        loadOptions={loadOptions ?? defaultLoadOptions}
        classNamePrefix="input-select"
        ref={ref}
        name={name}
        components={{
          Control,
          ValueContainer,
          ClearIndicator,
          DropdownIndicator,
          SingleValue,
          Group,
          Option,
          MenuList,
          MultiValue,
          MultiValueRemove,
          LoadingIndicator,
        }}
        isClearable={!readOnly && !!isClearable}
        menuPortalTarget={menuPortalTarget ?? portal}
        isDisabled={disabled || isLoading}
        isSearchable={!readOnly && isSearchable}
        isLoading={!readOnly && isLoading}
        menuShouldScrollIntoView
        menuIsOpen={readOnly ? false : undefined}
        isValidNewOption={(inputValue: string) => inputValue && isCreatable}
        noOptionsMessage={() => noOptionsMessage}
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 999,
          }),
          control: (base, state) => ({
            ...base,
            border: 'none',
            borderRadius: 0,
            outline: 'none',
            boxShadow: state.menuIsOpen
              ? '0px 0px 6px rgba(0, 0, 0, 0.2)'
              : 'none',
            clipPath: 'inset(0px -10px -4px -10px)',
            background: 'transparent',
            minHeight: size === 'sm' ? '40px' : '50px',
            flexWrap: 'nowrap',
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '4px 0',
            gap: '0.3125rem',
            fontSize: size === 'md' ? '14px' : '12px',
            lineHeight: size === 'md' ? '24px' : '18px',
            outline: 'none',
            minWidth: '50px',
          }),
          singleValue: (base) => ({
            ...base,
            width: 'fit-content',
            margin: 0,
            order: 1,
            color: 'black',
          }),
          placeholder: (base, state) => ({
            ...base,
            margin: '0',
            color: state.isDisabled ? '#000000' : '#6B6A69',
            opacity: state.isDisabled ? 0.5 : 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }),
          indicatorsContainer: (base) => ({
            ...base,
            columnGap: '10px',
            padding: '0 10px',
          }),
          indicatorSeparator: (base) => ({
            ...base,
            ...(!isClearable && { display: 'none' }),
          }),
          input: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
            margin: 0,
          }),
          menu: (base) => ({
            ...base,
            borderRadius: 0,
            border: 'none',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
            marginTop: props.isMulti ? '0px' : '-1px',
            width: renderOption ? undefined : '100%',
            minWidth: '100%',
            MsOverflowStyle: 'none' /* IE and Edge */,
            scrollbarWidth: 'none' /* Firefox */,
          }),
          menuList: (base) => ({
            ...base,
            padding: '0 0 5px',
            fontSize: '14px',
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: 0,
          }),
          option: (styles) => ({
            ...styles,
            backgroundColor: undefined,
            fontSize: size === 'md' ? '14px' : '12px',
            color: 'black',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            justifyContent: 'space-between',
            ':active': {
              ...styles[':active'],
              backgroundColor: undefined,
            },
          }),
          multiValueRemove: (styles) => ({
            ...styles,
            height: '100%',
            marginTop: 'auto',
            marginBottom: 'auto',
            ':hover': {
              ...styles[':hover'],
              backgroundColor: undefined,
            },
          }),
          loadingMessage: (styles) => ({
            ...styles,
            fontSize: 'inherit',
          }),
          noOptionsMessage: (styles) => ({
            ...styles,
            fontSize: 'inherit',
          }),
        }}
      />
    );
  }
);

FormSelect.displayName = 'FormSelect';
FormSelect.defaultProps = {
  size: 'sm',
  required: false,
  isError: false,
  readOnly: false,
  isCreatable: false,
  isSearchable: false,
  loadOptions: undefined,
};

export default FormSelect;
